import React, { useState } from 'react';
import { Loader2, PlayIcon } from 'lucide-react';
import { useStore } from './store';
import { PipelineResultsModal } from './components/PipelineResultsModal';
import FeedbackModal from './components/FeedbackModal';
import { apiFetch, API_CONFIG } from './config/api';

export const SubmitButton = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [results, setResults] = useState(null);
    const [feedback, setFeedback] = useState({ isOpen: false, type: 'info', title: '', message: '', details: '' });
    const { nodes, edges } = useStore();

    const minDelay = (promise, delay) => {
        const startTime = Date.now();
        return promise.then(result => {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, delay - elapsed);
            return new Promise(resolve => {
                setTimeout(() => resolve(result), remaining);
            });
        });
    };

    const submitPipeline = async () => {
        try {
            setIsLoading(true);

            const pipelineData = {
                nodes: nodes.map(node => ({
                    id: node.id,
                    type: node.type,
                    data: node.data || {},
                    position: node.position || { x: 0, y: 0 }
                })),
                edges: edges.map(edge => ({
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    sourceHandle: edge.sourceHandle || null,
                    targetHandle: edge.targetHandle || null
                }))
            };

            const fetchPromise = apiFetch(API_CONFIG.ENDPOINTS.PIPELINES.PARSE, {
                method: 'POST',
                body: JSON.stringify(pipelineData)
            });

            const response = await minDelay(fetchPromise, 1000);

            // apiFetch already handles error checking and throws appropriate errors

            const result = await response.json();
            setResults(result);
            setShowResults(true);

        } catch (error) {
            console.error('Pipeline submission error:', error);

            let title = "Pipeline Submission Failed";
            let message = "Failed to submit pipeline. ";
            let details = "";

            if (error.message.includes('fetch')) {
                message += "Please ensure the backend server is running.";
                details = "Connection failed - check if server is accessible";
            } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
                message += "Cannot connect to backend server. Please check if it's running.";
                details = "Network error - server may be offline";
            } else {
                message += error.message;
                details = error.stack || "No additional details available";
            }

            setFeedback({
                isOpen: true,
                type: 'error',
                title,
                message,
                details
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = () => {
        if (nodes.length === 0) {
            setFeedback({
                isOpen: true,
                type: 'warning',
                title: 'Empty Pipeline',
                message: 'No nodes found in pipeline. Please add some nodes before submitting.',
                details: 'A pipeline must contain at least one node to be processed'
            });
            return;
        }

        submitPipeline();
    };

    return (
        <>

            <button onClick={handleSubmit}
                disabled={isLoading} class="rounded relative inline-flex group items-center justify-center px-3.5 py-2 m-1 cursor-pointer border-b-4 border-l-2 active:border-purple-600 active:shadow-none shadow-lg bg-gradient-to-tr from-purple-600 to-purple-500 border-purple-700 text-white">
                <span class="absolute w-0 h-0 transition-all duration-300 ease-out bg-white rounded-full group-hover:w-32 group-hover:h-32 opacity-10"></span>
                <span class="relative flex items-center gap-1">{isLoading ? (
                    <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                    </>
                ) : (
                    <>
                        <PlayIcon className="w-4 h-4" />
                        Submit Pipeline
                    </>
                )}</span>
            </button>
            <PipelineResultsModal
                isOpen={showResults}
                onClose={() => setShowResults(false)}
                results={results}
            />

            <FeedbackModal
                isOpen={feedback.isOpen}
                onClose={() => setFeedback(prev => ({ ...prev, isOpen: false }))}
                type={feedback.type}
                title={feedback.title}
                message={feedback.message}
                details={feedback.details}
            />
        </>
    );
};